import { replaceAllInString } from '@bobjs/utils';
import {
  addDays as addDaysToDate,
  addHours as addHoursToDate,
  addMinutes as addMinutesToDate,
  addSeconds as addSecondsToDate,
  differenceInCalendarDays,
  differenceInSeconds,
  isSameDay,
} from 'date-fns';
import format from 'date-fns/format';
import frLocale from 'date-fns/locale/fr';

export function formatDate(date: Date): string {
  return format(date, 'dd/MM/yyyy', { locale: frLocale });
}

export function formatTime(date: Date, padHours = true): string {
  return replaceAllInString(
    format(date, `${padHours ? 'H' : ''}H/mm`, { locale: frLocale }),
    '/',
    'h'
  );
}

export function addSeconds(date: Date, seconds: number): Date {
  return addSecondsToDate(date, seconds);
}

export function addMinutes(date: Date, minutes: number): Date {
  return addMinutesToDate(date, minutes);
}

export function addHours(date: Date, hours: number): Date {
  return addHoursToDate(date, hours);
}

export function addDays(date: Date, days: number): Date {
  return addDaysToDate(date, days);
}

export function formatRelativeDate(date: Date, relativeTo?: Date): string {
  const relativeDate = relativeTo ? relativeTo : new Date();
  const distanceInDays: number = differenceInCalendarDays(date, relativeDate);
  if (distanceInDays === 0) return "Aujourd'hui";
  if (distanceInDays === 1) return 'Demain';
  if (distanceInDays === -1) return 'Hier';
  return formatDate(date);
}

export function formatRelativeTime(time: Date, relativeTo?: Date): string {
  const ONE_MINUTE = 60;
  const ONE_HOUR = ONE_MINUTE * 60 - ONE_MINUTE;
  const ONE_DAY = ONE_HOUR * 24 - ONE_HOUR;
  const relativeTime = relativeTo ? relativeTo : new Date();
  const distanceInSeconds: number = differenceInSeconds(time, relativeTime);
  if (!isSameDay(time, relativeTime))
    return formatRelativeDate(time, relativeTime);

  const isDistanceNegative = distanceInSeconds < 0;
  if (!isDistanceNegative && distanceInSeconds < ONE_MINUTE)
    return 'Il y a quelques secondes';
  if (!isDistanceNegative && distanceInSeconds < ONE_HOUR)
    return `Il y a ${Math.round(distanceInSeconds / ONE_MINUTE)} minute${
      Math.round(distanceInSeconds / ONE_MINUTE) === 1 ? '' : 's'
    }`;
  if (!isDistanceNegative && distanceInSeconds < ONE_DAY)
    return `Il y a ${Math.round(distanceInSeconds / ONE_HOUR)} heure${
      Math.round(distanceInSeconds / ONE_HOUR) === 1 ? '' : 's'
    }`;

  const distanceWhenInFuture = -distanceInSeconds;
  if (distanceWhenInFuture < ONE_MINUTE) return 'Dans quelques secondes';
  if (distanceWhenInFuture < ONE_HOUR)
    return `Dans ${Math.round(distanceWhenInFuture / ONE_MINUTE)} minute${
      Math.round(distanceInSeconds / ONE_MINUTE) === 1 ? '' : 's'
    }`;
  if (distanceWhenInFuture < ONE_DAY)
    return `Dans ${Math.round(distanceWhenInFuture / ONE_HOUR)} heure${
      Math.round(distanceWhenInFuture / ONE_HOUR) === 1 ? '' : 's'
    }`;
  return formatRelativeDate(time, relativeTime);
}
